import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';

import type { Result } from '@libs/models';
import { ResultFactory } from '@libs/models';

export type { Result };

const extractErrorMessage = (error: any, fallbackMessage: string): string => {
  const errorData = error?.response?.data;

  if (errorData?.message) {
    return errorData.message;
  }

  if (errorData?.errorTranslationCode) {
    return errorData.errorTranslationCode;
  }

  return error?.message ?? fallbackMessage;
};

const getApiBaseUrl = (): string => {
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_API_URL ?? 'http://localhost:5000';
  }

  return window.location.origin;
};

export abstract class BaseApi {
  protected abstract readonly baseUrl: string;

  protected async executeResponse<T>(
    apiCall: () => Promise<AxiosResponse<T>>,
    errorMessage: string
  ): Promise<Result<T>> {
    try {
      const response = await apiCall();
      return ResultFactory.success(response.data);
    } catch (error: any) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return ResultFactory.failure('REQUEST_CANCELED');
      }

      const message = extractErrorMessage(error, errorMessage);
      return ResultFactory.failure(message);
    }
  }

  protected async executeVoid(apiCall: () => Promise<AxiosResponse<any>>): Promise<void> {
    await apiCall();
  }

  protected get<T>(
    url: string,
    params?: any,
    errorMessage = 'Request failed',
    signal?: AbortSignal
  ): Promise<Result<T>> {
    const fullUrl = `${this.baseUrl}${url}`;
    const config: AxiosRequestConfig = {};

    if (params) {
      config.params = params;
    }

    if (signal) {
      config.signal = signal;
    }

    return this.executeResponse(() => getApiClient().get<T>(fullUrl, config), errorMessage);
  }

  protected post<T>(
    url: string,
    data?: any,
    errorMessage = 'Request failed',
    signal?: AbortSignal
  ): Promise<Result<T>> {
    const fullUrl = `${this.baseUrl}${url}`;
    const config: AxiosRequestConfig = {};

    if (signal) {
      config.signal = signal;
    }

    return this.executeResponse(() => getApiClient().post<T>(fullUrl, data, config), errorMessage);
  }

  protected put<T>(url: string, data?: any, errorMessage = 'Request failed'): Promise<Result<T>> {
    const fullUrl = `${this.baseUrl}${url}`;
    return this.executeResponse(() => getApiClient().put<T>(fullUrl, data), errorMessage);
  }

  protected patch<T>(url: string, data?: any, errorMessage = 'Request failed'): Promise<Result<T>> {
    const fullUrl = `${this.baseUrl}${url}`;
    return this.executeResponse(() => getApiClient().patch<T>(fullUrl, data), errorMessage);
  }

  protected delete<T>(
    url: string,
    params?: any,
    errorMessage = 'Request failed'
  ): Promise<Result<T>> {
    const fullUrl = `${this.baseUrl}${url}`;
    return this.executeResponse(
      () => getApiClient().delete<T>(fullUrl, params ? { params } : undefined),
      errorMessage
    );
  }

  protected postVoid(url: string, data?: any): Promise<void> {
    const fullUrl = `${this.baseUrl}${url}`;
    return this.executeVoid(() => getApiClient().post(fullUrl, data));
  }

  protected putVoid(url: string, data?: any): Promise<void> {
    const fullUrl = `${this.baseUrl}${url}`;
    return this.executeVoid(() => getApiClient().put(fullUrl, data));
  }

  protected patchVoid(url: string, data?: any): Promise<void> {
    const fullUrl = `${this.baseUrl}${url}`;
    return this.executeVoid(() => getApiClient().patch(fullUrl, data));
  }

  protected deleteVoid(url: string, params?: any): Promise<void> {
    const fullUrl = `${this.baseUrl}${url}`;
    return this.executeVoid(() => getApiClient().delete(fullUrl, params ? { params } : undefined));
  }

  protected buildUrl(path: string, ...params: (string | number)[]): string {
    let url = `${this.baseUrl}${path}`;
    params.forEach(param => {
      url = url.replace(/\{[^}]+\}/, String(param));
    });
    return url;
  }

  protected buildRouteUrl(route: string, params: Record<string, string | number>): string {
    let url = `${this.baseUrl}${route}`;
    Object.entries(params).forEach(([key, value]) => {
      url = url.replace(`{${key}}`, String(value));
    });
    return url;
  }
}

export class ApiClient {
  private readonly axiosInstance: AxiosInstance;
  private interceptorsSetup = false;
  private setupPromise: Promise<void> | null = null;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: getApiBaseUrl(),
      timeout: 30000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  private async setupInterceptors(): Promise<void> {
    if (this.interceptorsSetup) {
      return;
    }

    if (this.setupPromise) {
      return this.setupPromise;
    }

    this.setupPromise = this.performSetup();
    return this.setupPromise;
  }

  private async performSetup(): Promise<void> {
    try {
      const { setupInstanceInterceptors, getCurrentStore } = await import('Config/axios');

      const store = getCurrentStore();
      setupInstanceInterceptors(this.axiosInstance, store);

      this.interceptorsSetup = true;
    } catch (error) {
      console.warn('Failed to setup API interceptors:', error);
      this.interceptorsSetup = true;
    } finally {
      this.setupPromise = null;
    }
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    await this.setupInterceptors();
    return this.axiosInstance.get<T>(url, config);
  }

  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    await this.setupInterceptors();
    return this.axiosInstance.post<T>(url, data, config);
  }

  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    await this.setupInterceptors();
    return this.axiosInstance.put<T>(url, data, config);
  }

  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    await this.setupInterceptors();
    return this.axiosInstance.patch<T>(url, data, config);
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    await this.setupInterceptors();
    return this.axiosInstance.delete<T>(url, config);
  }

  getInstance(): AxiosInstance {
    return this.axiosInstance;
  }

  async getInstanceWithInterceptors(): Promise<AxiosInstance> {
    await this.setupInterceptors();
    return this.axiosInstance;
  }

  async setupStoreInterceptors(store: any): Promise<void> {
    if (!store) {
      console.warn('No store provided to setupStoreInterceptors');
      return;
    }

    try {
      const { setupInstanceInterceptors } = await import('Config/axios');

      this.axiosInstance.interceptors.request.clear();
      this.axiosInstance.interceptors.response.clear();

      setupInstanceInterceptors(this.axiosInstance, store);
      this.interceptorsSetup = true;
    } catch (error) {
      console.warn('Failed to setup store interceptors:', error);
    }
  }

  isSetup(): boolean {
    return this.interceptorsSetup;
  }

  resetInterceptors(): void {
    this.axiosInstance.interceptors.request.clear();
    this.axiosInstance.interceptors.response.clear();
    this.interceptorsSetup = false;
    this.setupPromise = null;
  }
}

let apiClientInstance: ApiClient | null = null;

export const getApiClient = (): ApiClient => {
  apiClientInstance ??= new ApiClient();
  return apiClientInstance;
};
